import { useCallback, useState, useEffect } from 'react';
import { ExecutionError, useManualGql } from '@search/gql-client/src';
import { QueryErrors } from '../types/QueryErrors';
import { ICustomerFields, ICustomer } from '../types/ICustomer';
import { AgentCreateCustomerMutation } from './AgentCreateCustomerMutation';
import { AgentCreateCustomerMutation$variables, AgentCreateCustomerMutation$data } from './__generated__/AgentCreateCustomerMutation.graphql';

interface IUseCreateCustomerReturnType {
    onCreate: (args: AgentCreateCustomerMutation$variables) => Promise<AgentCreateCustomerMutation$data>;
    createCustomerData?: AgentCreateCustomerMutation$data;
    createCustomerLoading: boolean;
    createCustomerErrors: readonly ExecutionError[] | string;
}

const useCreateCustomerGql = (): IUseCreateCustomerReturnType => {
    const [ errorOp, setErrorOp ] = useState('');

    const { load, data, loading: isLoading, errors } = useManualGql<
        AgentCreateCustomerMutation$variables,
        AgentCreateCustomerMutation$data & QueryErrors
    >(AgentCreateCustomerMutation);

    const onCreate = useCallback(
        (args: AgentCreateCustomerMutation$variables) => {
            return load(args)
                .then(res => {
                    if ((res.errors ?? null) !== null) throw res.errors;

                    return res;
                })
                .catch((errs: Error[] | Error) => {
                    const errorMesasge = errs.toString();

                    setErrorOp(errorMesasge);
                    throw errorMesasge;
                });
        },
        [ load ]
    );

    return {
        onCreate,
        createCustomerData: data ?? undefined,
        createCustomerLoading: isLoading,
        createCustomerErrors: errors ?? errorOp
    };
};

interface IUseCustomerCrate extends Omit<IUseCreateCustomerReturnType, 'onCreate'|'createCustomerErrors'> {
    onCreateCustomer: (customer: ICustomerFields) => Promise<ICustomer | undefined>;
}

export const useCustomerCreate = (): IUseCustomerCrate => {
    const { onCreate, createCustomerErrors, ...rest } = useCreateCustomerGql();

    useEffect(() => {
        const hasError = Boolean(createCustomerErrors);

        if (hasError) {
            // eslint-disable-next-line no-console
            console.error('useCustomerCreate error', createCustomerErrors);
        }
    }, [ createCustomerErrors ]);

    const onCreateCustomer = useCallback(
        (customer: ICustomerFields) => {
            return onCreate({
                ...customer
            })
                .then(res => {
                    const hasRes = Boolean(res);

                    if (hasRes && (res.businessCreateCustomer ?? null) !== null) {
                        return res.businessCreateCustomer;
                    }
                    // eslint-disable-next-line no-console
                    console.error('onCreateCustomer error');

                    return;
                })
                .catch((error: string) => {
                    // eslint-disable-next-line no-console
                    console.error('onCreateCustomer error', error);

                    throw error;
                });
        },
        [ onCreate ]
    );

    return {
        onCreateCustomer,
        ...rest
    };
};
